import { useState, useEffect } from 'react';
import { LIMITES } from '../limites';
import { CheckCircle2, Eye, HelpCircle, AlertTriangle, Trophy, Zap, Clock, Check } from 'lucide-react';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import './Exercise.css';
import './FillInTheBlankExercise.css';

const incorrectFeedbackMessages = ["Not quite, try again!", "Keep trying!", "Almost there!", "Give it another shot!"];
const getRandomMessage = (messages) => messages[Math.floor(Math.random() * messages.length)];

// <<< FUNCIÓN "TRADUCTORA" DE MARKDOWN (MANTENIDA) >>>
const parseAndHighlightText = (text) => {
  if (!text) return '';
  // Divide el texto en partes, separando lo que está en negrita.
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) => {
    // Las partes impares son las que estaban entre asteriscos.
    if (index % 2 === 1) {
      return <strong key={index} className="fib-highlight-text">{part}</strong>;
    }
    return part;
  });
};

function FillInTheBlankExercise({ exerciseData, onCorrectAnswer }) {
  const safeData = exerciseData || {};
  const isNewFormat = safeData.blanks && safeData.blanks.length > 0;
  const items = isNewFormat ? safeData.blanks : (safeData.sentences || []);
  const totalItems = items.length;

  // Inicializamos userAnswers y results como arreglos de arreglos para soportar múltiples respuestas
  const [userAnswers, setUserAnswers] = useState(items.map(item => Array(item.answers?.length || 1).fill('')));
  const [results, setResults] = useState(items.map(item => Array(item.answers?.length || 1).fill(null)));
  const [hints, setHints] = useState(Array(totalItems).fill(false));
  const [showSolution, setShowSolution] = useState(Array(totalItems).fill(false));
  const [feedbackMessages, setFeedbackMessages] = useState({});
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(safeData.timeLimit || 120);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    setUserAnswers(items.map(item => Array(item.answers?.length || 1).fill('')));
    setResults(items.map(item => Array(item.answers?.length || 1).fill(null)));
  }, [exerciseData, totalItems]);

  useEffect(() => {
    if (!isTimerActive) return;
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setIsTimerActive(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isTimerActive]);

  useEffect(() => {
    // Contamos las preguntas completadas (todas las respuestas de una pregunta deben ser correctas)
    const correctCount = results.reduce((count, resultArray) => {
      return count + (resultArray.every(r => r === 'correct') ? 1 : 0);
    }, 0);
    setCompletedCount(correctCount);

    if (correctCount === totalItems && totalItems > 0) {
      setIsTimerActive(false);
      onCorrectAnswer();
      launchConfetti();
      showAchievement('¡Diálogo completado!', '💬');
    }
  }, [results, totalItems, onCorrectAnswer]);

  const handleInputChange = (index, subIndex, value) => {
    const newAnswers = [...userAnswers];
    newAnswers[index] = [...newAnswers[index]];
    newAnswers[index][subIndex] = value;
    setUserAnswers(newAnswers);

    if (results[index][subIndex]) {
      const newResults = [...results];
      newResults[index] = [...newResults[index]];
      newResults[index][subIndex] = null;
      setResults(newResults);
      setFeedbackMessages(prev => ({...prev, [`${index}-${subIndex}`]: null}));
    }
  };

  const validateAnswer = (index) => {
    const item = isNewFormat ? safeData.blanks[index] : safeData.sentences[index];
    
    // Corregido: Aseguramos que 'answer' y 'answers' sean strings antes de trim/toLowerCase.
    const correctAnswers = item.answers 
      ? item.answers.map(a => String(a || '').trim().toLowerCase()) 
      : [String(item.answer || '').trim().toLowerCase()];
    
    // #############################################################
    // ### CORRECCIÓN CLAVE: Manejo de respuestas alternativas (ahora soporta Array de Arrays)
    // #############################################################
    
    let altAnswersRaw = item.alternate_answers || item.alternate_answer;

    // Si altAnswersRaw es un Array de Arrays (para múltiples inputs), lo aplanamos una vez, 
    // de lo contrario, lo tratamos como un solo elemento o un Array de Strings.
    let consolidatedAlts = [];
    if (Array.isArray(altAnswersRaw)) {
      // Si es Array de Arrays (como en S3), lo aplanamos para obtener solo las strings.
      if (altAnswersRaw.length > 0 && Array.isArray(altAnswersRaw[0])) {
        consolidatedAlts = altAnswersRaw.flat();
      } else {
        // Si es solo un Array de Strings (como en S2/S5)
        consolidatedAlts = altAnswersRaw;
      }
    } else if (altAnswersRaw) {
      // Si es un solo String (alternate_answer singular)
      consolidatedAlts = [altAnswersRaw];
    }

    // Limpiamos y convertimos a minúsculas, asegurando que sean strings para evitar TypeError
    const alternateAnswers = consolidatedAlts.map(a => String(a || '').trim().toLowerCase());
    
    // #############################################################

    const userAnswerArray = userAnswers[index].map(a => a?.trim().toLowerCase() || '');

    // #############################################################
    // ### Lógica de salida para evitar validar inputs incompletos.
    // #############################################################
    const hasEmptyInput = userAnswerArray.some(a => !a);
    const isEmptyAllowed = alternateAnswers.includes('');

    // Si hay algún input vacío Y el vacío NO es una respuesta permitida, salimos sin validar.
    if (hasEmptyInput && !isEmptyAllowed) {
        return; 
    }
    // #############################################################


    const newResults = [...results];
    newResults[index] = [...newResults[index]];

    let allCorrect = true;
    userAnswerArray.forEach((userAnswer, subIndex) => {
      const correctAnswer = correctAnswers[subIndex];
      
      // Si la respuesta es la principal
      let isCorrect = userAnswer === correctAnswer;
      
      // Verificamos si la respuesta del usuario coincide con alguna alternativa para ESE subIndex.
      // Ya que los alternate_answers son ahora una lista PLANA, debemos buscar si la respuesta
      // del usuario para ESTE subIndex está entre las alternativas para ESTE subIndex.
      
      // Nota: Si S3 solo tiene una alternativa general para todas las respuestas, usamos la lista plana.
      // Si el JSON se extiende para dar alternativas específicas por input, se necesita más lógica,
      // pero por ahora, la lógica general es la más segura.
      if (!isCorrect) {
          // Si el JSON original tiene una lista plana de alternativas GENERALES (como 'that' para which),
          // esta es la verificación:
          isCorrect = alternateAnswers.includes(userAnswer);
          
          // Nota: Si quieres que el sistema maneje alternativas ESPECÍFICAS por input, 
          // la estructura de tu JSON para 'alternate_answers' DEBE ser revisada. 
          // Asumiendo que las alternativas son GENERALES por ahora:
      }

      newResults[index][subIndex] = isCorrect ? 'correct' : 'incorrect';
      if (!isCorrect) {
        allCorrect = false;
        setFeedbackMessages(prev => ({...prev, [`${index}-${subIndex}`]: getRandomMessage(incorrectFeedbackMessages)}));
      } else {
        setFeedbackMessages(prev => ({...prev, [`${index}-${subIndex}`]: null}));
      }
    });

    setResults(newResults);

    if (allCorrect) {
      setPoints(p => p + 10);
      setStreak(s => s + 1);
    } else {
      setStreak(0);
    }
  };

  const handleShowSolution = (index) => {
    const newShowSolution = [...showSolution];
    newShowSolution[index] = true;
    setShowSolution(newShowSolution);
    setTimeout(() => {
      setShowSolution(currentShowSolution => currentShowSolution.map((val, i) => i === index ? false : val));
    }, 4000);
  };

  const handleKeyDown = (e, index, subIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAnswer(index);
    }
  };

  const toggleHint = (index) => {
    const newHints = [...hints];
    newHints[index] = !newHints[index];
    setHints(newHints);
  };

  const progressPercentage = totalItems > 0 ? (completedCount / totalItems) * 100 : 0;

  return (
    <div className="exercise-container">
      <div className="fib-info-bar">
        <div className="fib-info-item"><span>🏆</span> {points} pts</div>
        <div className="fib-info-sub-bar">
          <div className="fib-info-item"><span>⚡</span> Racha: {streak}</div>
          <div className={`fib-info-item ${timeLeft <= 10 ? 'accent-bg' : ''}`}><span>🕒</span> {timeLeft}s</div>
        </div>
          </div>
      <div className="fib-progress-bar-container">
        <div className="fib-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
      </div>
      <header className="fib-header">
        <h2 className="exercise-title">{safeData.title || 'Completa el Diálogo'}</h2>
        <p className="exercise-instruction">{safeData.instruction || 'Rellena los espacios en blanco.'}</p>
      </header>
      <div className="fib-sentences-wrapper">
        {items.map((item, index) => {
          let beforeText = '';
          let afterText = '';
          const answers = item.answers || [item.answer]; // Soporte para preguntas con un solo answer

          if (isNewFormat) {
            beforeText = item.before_blank || '';
            afterText = item.after_blank || '';
          } else {
            const parts = item.start.split('___');
            beforeText = parts[0];
            afterText = (parts[1] || '') + (item.end || '');
          }

          const textParts = answers.length > 1 ? [beforeText, afterText].join('___').split('___') : [beforeText, afterText];
          const displayParts = [];
          let partIndex = 0;

          answers.forEach((_, subIndex) => {
            if (partIndex < textParts.length) {
              displayParts.push(textParts[partIndex]);
              partIndex++;
            }
          });
          if (partIndex < textParts.length) {
            displayParts.push(textParts.slice(partIndex).join(''));
          }

          return (
            <div key={item.id || index} className={`fib-sentence-card ${results[index].every(r => r === 'correct') ? 'correct' : results[index].some(r => r === 'incorrect') ? 'incorrect' : ''}`}>
              <div className="fib-sentence-content">
                {item.emoji && <span className="fib-emoji-hint">{item.emoji}</span>}
                {answers.map((answer, subIndex) => (
                  <span key={subIndex}>
                    {subIndex === 0 && parseAndHighlightText(displayParts[0])}
                    <input
                        maxLength={LIMITES.respuestaMinijuego}
                      type="text"
                      className="fib-blank-input"
                      placeholder="..."
                      // <<< CIRUGÍA PRECISA AQUÍ: Añadimos el atributo 'size' >>>
                      size={answer?.length > 4 ? answer.length + 2 : 6}
                      value={userAnswers[index][subIndex] || ''}
                      onChange={(e) => handleInputChange(index, subIndex, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, index, subIndex)}
                      onBlur={() => validateAnswer(index)}
                      disabled={results[index][subIndex] === 'correct'}
                    />
                    {subIndex < answers.length - 1 && parseAndHighlightText(displayParts[subIndex + 1])}
                    {subIndex === answers.length - 1 && parseAndHighlightText(displayParts[subIndex + 1])}
                  </span>
                ))}
              </div>
              <div className="fib-sentence-controls">
                <div className="fib-controls-left">
                  {results[index].every(r => r === 'correct') ? (
                    <button className="fib-verify-button correct-state" disabled>
                      <Check size={18} /> Correct!
                    </button>
                  ) : (
                    <button
                      className="fib-verify-button"
                      onClick={() => validateAnswer(index)}
                      disabled={results[index].every(r => r === 'correct')}
                    >
                      <CheckCircle2 size={18} /> Check
                    </button>
                  )}
                  {results[index].some(r => r === 'incorrect') && (
                    <button 
                      className="fib-solution-button" 
                      onClick={() => handleShowSolution(index)}
                      disabled={showSolution[index]}
                    >
                      <AlertTriangle size={18} /> Show Solution
                    </button>
                  )}
                </div>
                <div className="fib-controls-right">
                  {item.hint && (
                    <button className="fib-hint-button" onClick={() => toggleHint(index)}>
                      <Eye size={18} />
                    </button>
                  )}
                </div>
              </div>
              {(results[index].some(r => r === 'incorrect') || hints[index] || showSolution[index]) && (
                <div className="fib-feedback-area">
                  <hr className="fib-separator" />
                  {results[index].some(r => r === 'incorrect') && !showSolution[index] && (
                    <div className="fib-feedback-message incorrect">
                      <HelpCircle size={16} /> {feedbackMessages[`${index}-0`] || 'Try again!'}
                    </div>
                  )}
                  {showSolution[index] && (
                    <div className="fib-feedback-message solution">
                      Solution: <strong>{answers.join(' ')}</strong>
                    </div>
                  )}
                  {hints[index] && (
                    <div className="fib-feedback-message hint">
                      <HelpCircle size={16} /> Pista: {item.hint}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FillInTheBlankExercise;